import { type FocusEventHandler, type ReactNode } from "react";
import { NumericFormat, type OnValueChange } from "react-number-format";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type NumberInputProps = {
  autofocus?: boolean;
  className?: string;
  decimalScale?: number;
  disabled?: boolean;
  errorWithTouchedMode?: boolean;
  withBorder?: boolean;
  touched?: boolean;
  color?: string;
  error?: string;
  isOptional?: boolean;
  label?: string;
  labelTag?: ReactNode;
  leftLabel?: ReactNode;
  name: string;
  onBlur?: FocusEventHandler;
  onChange?: OnValueChange;
  placeholder?: string;
  thousandSeparator?: "," | ".";
  value: number | string;
  wrapperClassName?: string;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

export function NumberInput({
  autofocus,
  decimalScale,
  disabled,
  error,
  errorWithTouchedMode,
  withBorder = true,
  isOptional,
  label,
  labelTag,
  leftLabel,
  name,
  color,
  onBlur,
  onChange,
  placeholder,
  thousandSeparator,
  touched,
  value,
  fullWidth,
  constrainInputWidth = false,
  wrapperClassName,
}: NumberInputProps) {
  const errorVisible = isTruthy(errorWithTouchedMode)
    ? isTruthy(error) && touched
    : error;

  return (
    <div className={cnTwMerge("relative", fullWidth && "w-full")}>
      <div className={cnTwMerge(fullWidth && "w-full")}>
        <FormLabel isOptional={isOptional} label={label} tag={labelTag} />
      </div>
      <div
        className={cnTwMerge(
          "relative flex text-text/primary rounded-companyBorderRadius border transition focus-within:border-brand-default hover:not-disabled:border-brand-default min-h-11",
          wrapperClassName,
          {
            "max-w-[50%]": constrainInputWidth,
            "!border-border/negative": errorVisible,
            "border-border/primary": !!withBorder,
            "border-transparent": !withBorder,
            "text-text/secondary": color,
          },
        )}
      >
        {leftLabel ? (
          <label
            className="shrink-0 flex items-center justify-center body-base-regular px-3 min-w-14"
            htmlFor={name}
          >
            <span>{leftLabel}</span>
          </label>
        ) : null}
        <NumericFormat
          allowNegative={false}
          aria-label={label}
          autoComplete="off"
          autoFocus={autofocus}
          className="block w-full text-sm bg-transparent border-0
          shadow-none appearance-none shadow-transparent placeholder:text-text/disabled
           focus:border-transparent focus:ring-0 focus:ring-offset-0
           disabled:bg-background/component/10 disabled:cursor-not-allowed"
          data-testid={`${name}-number-input`}
          decimalScale={decimalScale ?? 0}
          disabled={disabled}
          id={name}
          name={name}
          onBlur={onBlur}
          onValueChange={onChange}
          placeholder={placeholder ?? m.placeholders_inputNumber()}
          thousandSeparator={thousandSeparator}
          value={value}
        />
      </div>
      {isTruthy(error) ? (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      ) : null}
    </div>
  );
}
